import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Alert,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { PrivateWaypoint, WaypointCategory, GeoCoordinate } from '../types';
import { PrivateWaypointsService } from '../services/privateWaypoints';
import { toast } from '../services/toastService';
// Define colors
const colors = {
  primary: '#007AFF',
  secondary: '#5856D6',
  success: '#34C759',
  warning: '#FF9500',
  error: '#FF3B30',
  info: '#5AC8FA',
  background: '#F2F2F7',
  surface: '#FFFFFF',
  text: '#000000',
  textSecondary: '#8E8E93',
  border: '#E5E5EA',
};

// Define fonts
const fonts = {
  regular: 'System',
  semiBold: 'System',
  bold: 'System',
};

interface WaypointEditModalProps {
  waypoint: PrivateWaypoint | null;
  visible: boolean;
  onClose: () => void;
  onSave: (waypoint: PrivateWaypoint) => void;
  initialCoordinates?: GeoCoordinate;
}

const categories: { value: WaypointCategory; label: string; icon: string }[] = [
  { value: 'fishing_spot', label: 'Fishing Spot', icon: 'phishing' },
  { value: 'reef', label: 'Reef', icon: 'terrain' },
  { value: 'wreck', label: 'Wreck', icon: 'directions-boat' },
  { value: 'structure', label: 'Structure', icon: 'account-balance' },
  { value: 'anchorage', label: 'Anchorage', icon: 'anchor' },
  { value: 'hazard', label: 'Hazard', icon: 'warning' },
  { value: 'personal', label: 'Personal', icon: 'person-pin' },
  { value: 'other', label: 'Other', icon: 'place' },
];

const WaypointEditModal: React.FC<WaypointEditModalProps> = ({
  waypoint,
  visible,
  onClose,
  onSave,
  initialCoordinates,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<WaypointCategory>('personal');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [depth, setDepth] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (waypoint) {
      setName(waypoint.name);
      setDescription(waypoint.description || '');
      setCategory(waypoint.category);
      setLatitude(waypoint.coordinates.latitude.toString());
      setLongitude(waypoint.coordinates.longitude.toString());
      setDepth(waypoint.metadata?.depth?.toString() || '');
      setNotes(waypoint.metadata?.notes || '');
    } else if (initialCoordinates) {
      setLatitude(initialCoordinates.latitude.toString());
      setLongitude(initialCoordinates.longitude.toString());
      setName('');
      setDescription('');
      setCategory('personal');
      setDepth('');
      setNotes('');
    }
  }, [waypoint, initialCoordinates]);

  const handleSave = async () => {
    if (!name.trim()) {
      Alert.alert('Error', 'Please enter a waypoint name');
      return;
    }

    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);

    if (isNaN(lat) || isNaN(lon)) {
      Alert.alert('Error', 'Please enter valid coordinates');
      return;
    }

    if (lat < -90 || lat > 90) {
      Alert.alert('Error', 'Latitude must be between -90 and 90');
      return;
    }

    if (lon < -180 || lon > 180) {
      Alert.alert('Error', 'Longitude must be between -180 and 180');
      return;
    }

    setSaving(true);

    try {
      const metadata: any = {};
      if (depth && depth.trim()) {
        metadata.depth = parseFloat(depth);
      }
      if (notes && notes.trim()) {
        metadata.notes = notes.trim();
      }

      const waypointData: Omit<PrivateWaypoint, 'id' | 'userId' | 'createdAt' | 'updatedAt'> = {
        name: name.trim(),
        category,
        coordinates: {
          latitude: lat,
          longitude: lon,
        },
        isVisible: true,
      };

      // Only add optional fields if they have values
      if (description.trim()) {
        waypointData.description = description.trim();
      }
      if (Object.keys(metadata).length > 0) {
        waypointData.metadata = metadata;
      }

      let savedWaypoint: PrivateWaypoint;

      if (waypoint) {
        await PrivateWaypointsService.updateWaypoint(waypoint.id, waypointData);
        savedWaypoint = { ...waypoint, ...waypointData, updatedAt: new Date().toISOString() };
        console.log('[WaypointEditModal] Waypoint updated:', savedWaypoint.name);
        toast.success(`Waypoint "${name.trim()}" updated successfully`);
        Alert.alert('Success', `Waypoint "${name.trim()}" updated successfully`);
      } else {
        savedWaypoint = await PrivateWaypointsService.createWaypoint(waypointData);
        console.log('[WaypointEditModal] Waypoint created:', savedWaypoint.name);
        toast.success(`Waypoint "${name.trim()}" saved successfully`);
        Alert.alert('Success', `Waypoint "${name.trim()}" saved successfully`);
      }

      onSave(savedWaypoint);
    } catch (error) {
      console.error('Error saving waypoint:', error);
      Alert.alert('Error', 'Failed to save waypoint');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.modalContainer}
      >
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>
              {waypoint ? 'Edit Waypoint' : 'New Waypoint'}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Icon name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.form} showsVerticalScrollIndicator={false}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Name *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter waypoint name"
                placeholderTextColor={colors.textSecondary}
                value={name}
                onChangeText={setName}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Description</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Enter description"
                placeholderTextColor={colors.textSecondary}
                value={description}
                onChangeText={setDescription}
                multiline
                numberOfLines={3}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Category</Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                style={styles.categoryScroll}
              >
                {categories.map((cat) => (
                  <TouchableOpacity
                    key={cat.value}
                    style={[
                      styles.categoryButton,
                      category === cat.value && styles.categoryButtonActive,
                    ]}
                    onPress={() => setCategory(cat.value)}
                  >
                    <Icon
                      name={cat.icon}
                      size={20}
                      color={category === cat.value ? colors.background : colors.text}
                    />
                    <Text
                      style={[
                        styles.categoryButtonText,
                        category === cat.value && styles.categoryButtonTextActive,
                      ]}
                    >
                      {cat.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            <View style={styles.coordinatesRow}>
              <View style={[styles.inputGroup, styles.coordinateInput]}>
                <Text style={styles.label}>Latitude *</Text>
                <TextInput
                  style={styles.input}
                  placeholder="00.000000"
                  placeholderTextColor={colors.textSecondary}
                  value={latitude}
                  onChangeText={setLatitude}
                  keyboardType={Platform.OS === 'ios' ? 'numbers-and-punctuation' : 'numeric'}
                />
              </View>
              <View style={[styles.inputGroup, styles.coordinateInput]}>
                <Text style={styles.label}>Longitude *</Text>
                <TextInput
                  style={styles.input}
                  placeholder="-00.000000"
                  placeholderTextColor={colors.textSecondary}
                  value={longitude}
                  onChangeText={setLongitude}
                  keyboardType={Platform.OS === 'ios' ? 'numbers-and-punctuation' : 'numeric'}
                />
              </View>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Depth (feet)</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter depth"
                placeholderTextColor={colors.textSecondary}
                value={depth}
                onChangeText={setDepth}
                keyboardType="numeric"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Notes</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Additional notes..."
                placeholderTextColor={colors.textSecondary}
                value={notes}
                onChangeText={setNotes}
                multiline
                numberOfLines={4}
              />
            </View>
          </ScrollView>

          <View style={styles.modalFooter}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              onPress={onClose}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.saveButton]}
              onPress={handleSave}
              disabled={saving}
            >
              <Text style={styles.saveButtonText}>
                {saving ? 'Saving...' : 'Save'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    backgroundColor: colors.background,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.25,
        shadowRadius: 10,
      },
      android: {
        elevation: 20,
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalTitle: {
    fontSize: 20,
    fontFamily: fonts.bold,
    color: colors.text,
  },
  closeButton: {
    padding: 4,
  },
  form: {
    padding: 20,
  },
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontFamily: fonts.semiBold,
    color: colors.text,
    marginBottom: 8,
  },
  input: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    fontFamily: fonts.regular,
    color: colors.text,
    borderWidth: 1,
    borderColor: colors.border,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  categoryScroll: {
    flexDirection: 'row',
  },
  categoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: colors.surface,
    marginRight: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  categoryButtonActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  categoryButtonText: {
    fontSize: 14,
    fontFamily: fonts.regular,
    color: colors.text,
    marginLeft: 6,
  },
  categoryButtonTextActive: {
    color: colors.background,
  },
  coordinatesRow: {
    flexDirection: 'row',
    gap: 12,
  },
  coordinateInput: {
    flex: 1,
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: 12,
  },
  button: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  saveButton: {
    backgroundColor: colors.primary,
  },
  cancelButtonText: {
    fontSize: 16,
    fontFamily: fonts.semiBold,
    color: colors.text,
  },
  saveButtonText: {
    fontSize: 16,
    fontFamily: fonts.semiBold,
    color: colors.background,
  },
});

export default WaypointEditModal;